import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

export interface ClassifiedCartItem {
  listingId: string;
  name: string;
  price: number;
  qty: number;

  hostId: string;   
  brandId: string; 
}


interface ClassifiedServiceFeeState {
  items: ClassifiedCartItem[];

  subtotal: number;
  totalQty: number;

  guestsFee: number;
  hostsFee: number;
  platformProfit: number;
  totalCharged: number;
deliveryTotal: number;
  calculated: boolean;
  hostProfits: Record<
  string,
  {
    saleTotal: number;
    deliveryTotal: number;
    hostsFee: number;
    hostProfit: number;
  }
>;

}

/* ─────────────────────────────────────────────
   UTILITY FUNCTIONS (IDENTICAL)
───────────────────────────────────────────── */

export const calculateHostEarnings = (total: number) => {
  let hostFeePercentage: number;

  if (total > 5000) hostFeePercentage = 0.2;
  else if (total > 1000) hostFeePercentage = 0.15;
  else if (total > 500) hostFeePercentage = 0.12;
  else if (total > 100) hostFeePercentage = 0.1;
  else hostFeePercentage = 0.08;

  const hostsFee = parseFloat((total * hostFeePercentage).toFixed(2));
  const hostProfit = parseFloat((total - hostsFee).toFixed(2));

  return { hostProfit, hostsFee };
};

export const calculateGuestServiceFee = (subtotal: number): number => {
  let guestsFee: number;

if (subtotal > 5000) guestsFee = subtotal * 0.2;
else if (subtotal > 1000) guestsFee = subtotal * 0.15;
else if (subtotal > 500) guestsFee = subtotal * 0.12;
else if (subtotal > 100) guestsFee = subtotal * 0.1;
else guestsFee = subtotal * 0.08;

  return parseFloat(guestsFee.toFixed(2));
};

export const calculatePlatformProfit = (
  hostsFee: number,
  guestsFee: number
): number =>
  parseFloat((hostsFee + guestsFee).toFixed(2));

/* ─────────────────────────────────────────────
   ASYNC CALCULATION THUNK
───────────────────────────────────────────── */
export const calculateClassifiedEarnings = createAsyncThunk(
  "classifiedCommerceServiceFees/calculateClassifiedEarnings",
  async ({
    items,
    deliveryByBrand,
  }: {
    items: ClassifiedCartItem[];
    deliveryByBrand: Record<string, number>;
  }) => {

    const groupedByHost: Record<string, ClassifiedCartItem[]> = {};

    items.forEach((item) => {
      if (!groupedByHost[item.hostId]) {
        groupedByHost[item.hostId] = [];
      }
      groupedByHost[item.hostId].push(item);
    });

    const hostProfits: any = {};

    let subtotal = 0;
    let totalQty = 0;
    let globalHostsFee = 0;
    let globalDelivery = 0;

    for (const hostId in groupedByHost) {
      const hostItems = groupedByHost[hostId];

      const saleTotal = hostItems.reduce(
        (s, i) => s + i.qty * i.price,
        0
      );

      const hostDelivery = hostItems.reduce((sum, item) => {
        return sum + (deliveryByBrand[item.brandId] ?? 0);
      }, 0);

      const { hostsFee } = calculateHostEarnings(saleTotal);

      const hostProfit = parseFloat(
        (saleTotal - hostsFee + hostDelivery).toFixed(2)
      );

      hostProfits[hostId] = {
        saleTotal,
        deliveryTotal: hostDelivery,
        hostsFee,
        hostProfit,
      };

      subtotal += saleTotal;
      totalQty += hostItems.reduce((s, i) => s + i.qty, 0);
      globalHostsFee += hostsFee;
      globalDelivery += hostDelivery;
    }

    const guestsFee = calculateGuestServiceFee(subtotal);
    const platformProfit = calculatePlatformProfit(
      globalHostsFee,
      guestsFee
    );

    const totalCharged = parseFloat(
      (subtotal + guestsFee + globalDelivery).toFixed(2)
    );

    return {
      items,
      totalQty,
      subtotal,
      deliveryTotal: globalDelivery,
      guestsFee,
      hostsFee: globalHostsFee,
      hostProfit: subtotal - globalHostsFee + globalDelivery,
      platformProfit,
      totalCharged,
      hostProfits,
    };
  }
);



/* ─────────────────────────────────────────────
   SLICE
───────────────────────────────────────────── */

const initialState: ClassifiedServiceFeeState = {
  items: [],
  subtotal: 0,
  totalQty: 0,
hostProfits: {},

  guestsFee: 0,
  hostsFee: 0,
  platformProfit: 0,
  totalCharged: 0,

  calculated: false,
  deliveryTotal: 0
};

const classifiedCommerceServiceFeeSlice = createSlice({
  name: "classifiedCommerceServiceFees",
  initialState,
  reducers: {
    resetClassifiedCommerceServiceFees: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(
      calculateClassifiedEarnings.fulfilled,
      (state, action) => {
        state.items = action.payload.items;
        state.totalQty = action.payload.totalQty;
        state.subtotal = action.payload.subtotal;

        state.guestsFee = action.payload.guestsFee;
        state.hostsFee = action.payload.hostsFee;
        state.platformProfit = action.payload.platformProfit;
        state.totalCharged = action.payload.totalCharged;
state.deliveryTotal = action.payload.deliveryTotal;
state.hostProfits = action.payload.hostProfits;

        state.calculated = true;
      }
    );
  },
});

export const { resetClassifiedCommerceServiceFees } =
  classifiedCommerceServiceFeeSlice.actions;

export default classifiedCommerceServiceFeeSlice.reducer;
